import {
    EligibilityDashboardFilter,
    EligibilityDistributionDto,
} from "generated/openApi";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import ApiService from "services/apiService";

interface EligibilityFilter {
    schoolYear: number | undefined;
    programId: number | undefined;
}

interface EligibilityDataContextValue {
    data: EligibilityDistributionDto[] | undefined;
    loading: boolean;
    error: string | null;
    activeSchoolYear: number | undefined;
    filter: EligibilityFilter;
    updateFilter: (updates: Partial<EligibilityFilter>) => void;
    refetch: () => void;
}

const EligibilityDataContext = createContext<EligibilityDataContextValue | undefined>(undefined);

interface EligibilityDataProviderProps {
    children: ReactNode;
}

export const EligibilityDataProvider = ({ children }: EligibilityDataProviderProps) => {
    const [data, setData] = useState<EligibilityDistributionDto[] | undefined>(undefined);
    const [activeSchoolYear, setActiveSchoolYear] = useState<number | undefined>(undefined);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [initialized, setInitialized] = useState(false);
    const [filter, setFilter] = useState<EligibilityFilter>({
        schoolYear: undefined,
        programId: undefined,
    });

    const fetchData = () => {
        setLoading(true);
        setError(null);
        const apiFilter: EligibilityDashboardFilter = {
            schoolYear: filter.schoolYear,
            programId: filter.programId,
        };
        ApiService.eligibilityDashboard
            .getEligibilityDistribution(apiFilter)
            .then((res) => {
                setData(res.data);
                setActiveSchoolYear(res.activeSchoolYear);

                if (!initialized) {
                    setInitialized(true);
                    if (!filter.schoolYear && res.activeSchoolYear) {
                        setFilter((prev) => ({
                            ...prev,
                            schoolYear: res.activeSchoolYear,
                        }));
                    }
                }
            })
            .catch((err) => {
                console.error("Failed to fetch eligibility dashboard:", err);
                setError("Failed to load eligibility data.");
            })
            .finally(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        if (initialized) {
            fetchData();
        }
    }, [filter]);

    const updateFilter = (updates: Partial<EligibilityFilter>) => {
        setFilter((prev) => ({ ...prev, ...updates }));
    };

    const value: EligibilityDataContextValue = {
        data,
        loading,
        error,
        activeSchoolYear,
        filter,
        updateFilter,
        refetch: fetchData,
    };

    return (
        <EligibilityDataContext.Provider value={value}>
            {children}
        </EligibilityDataContext.Provider>
    );
};

export const useEligibilityData = (): EligibilityDataContextValue => {
    const context = useContext(EligibilityDataContext);
    if (context === undefined) {
        throw new Error("useEligibilityData must be used within an EligibilityDataProvider");
    }
    return context;
};